// @flow
/* eslint arrow-parens:0 */
import * as React from 'react';
import { findDOMNode } from 'react-dom';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
// eslint-disable-next-line pinterest/no-deprecated-imports
import LegacyToolTipPulsar from 'app/common/react/ui/deprecated/LegacyToolTipPulsar';
import toCamelCase from 'app/common/lib/util/toCamelCase';
import type { Experience } from 'app/common/lib/react/experiences/experienceUtils';
import type { ExperienceClientProps } from 'app/common/lib/react/experiences/withExperienceClient';
import type { State as Experiences } from 'app/common/redux/reducers/experiences';
import withExperienceClient from 'app/common/lib/react/experiences/withExperienceClient';
import { deepMapStringKeys } from 'app/common/lib/util/ObjectUtils';
import { getExperienceAnchor } from 'app/common/lib/react/experiences/experienceUtils';
import { Box, Touchable } from 'gestalt';

// separating out the function for testing
export function getMapStateToProps(anchor: string, passedExperience?: Experience) {
  return (state: { experiences: Experiences }, ownProps: Object) => {
    if (passedExperience) {
      return { experience: passedExperience };
    }
    const experience = state.experiences.anchorExperiences[anchor];
    if (experience) {
      return { experience };
    }
    return {};
  };
}

type ExperienceAnchorPropTypes = ExperienceClientProps & {
  centerTooltip?: boolean,
  experience: Experience,
  isPulsarDisplayed?: boolean,
  onClick: Function,
  overflow: 'normal' | 'breakWord',
};

type ExperienceAnchorState = {
  hasModal: boolean,
  isPresentationDelayed: boolean,
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  wrappedComponentRef: ?React.Component<*, *>,
};

// separating out the class for testing
export function getWrapperComponent<C: React.ComponentType<any>>(
  anchor: string,
  WrappedComponent: C,
) {
  return class WrapperComponent extends React.Component<
    ExperienceAnchorPropTypes,
    ExperienceAnchorState,
  > {
    static defaultProps = { isPulsarDisplayed: true };

    constructor(props: ExperienceAnchorPropTypes) {
      super(props);
      const { experience } = props;
      let isPresentationDelayed = false;
      if (experience) {
        if (experience.display_data && experience.display_data.delay) {
          isPresentationDelayed = true;
        }
      }

      this.state = {
        hasModal: experience && experience.display_data && experience.display_data.modal,
        isPresentationDelayed,
        wrappedComponentRef: undefined,
      };
    }

    componentDidMount() {
      const { experience, experienceClient } = this.props;
      const { isPresentationDelayed } = this.state;
      if (experience) {
        if (isPresentationDelayed) {
          this.viewTimer = setTimeout(() => {
            this.showExperience();
          }, experience.display_data.delay);
        } else {
          experienceClient.viewExperience(experience.placement_id, experience.experience_id);
        }
      }
    }

    UNSAFE_componentWillReceiveProps(nextProps: ExperienceAnchorPropTypes) {
      const { experience } = this.props;
      const { experience: newExperience } = nextProps;
      if (!experience && newExperience) {
        const newState = {};
        if (newExperience.display_data && newExperience.display_data.delay) {
          newState.isPresentationDelayed = true;
          this.viewTimer = setTimeout(() => {
            this.showExperience();
          }, newExperience.display_data.delay);
        }
        if (newExperience.display_data && newExperience.display_data.modal) {
          newState.hasModal = true;
        }
        if (!isEmpty(newState)) {
          this.setState(newState);
        }
      }
    }

    componentWillUnmount() {
      if (this.viewTimer) {
        clearTimeout(this.viewTimer);
      }
    }

    getDisplayDataProps = () => {
      const { display_data: displayData } = this.props.experience;

      if (displayData.tooltip) {
        // flatten tooltip display data for backwards compatibility
        const tooltipDisplayData = {
          ...displayData.tooltip.options,
          ...displayData.tooltip,
        };
        // assign flattened tooltip display data back to the displayData
        displayData.tooltip = tooltipDisplayData;
        // no need to duplicate tooltip options
        delete displayData.tooltip.options;
      }

      return deepMapStringKeys(toCamelCase, true)(displayData);
    };

    viewTimer: TimeoutID | null = null;

    showExperience() {
      const { experience, experienceClient } = this.props;
      if (experience) {
        experienceClient.viewExperience(experience.placement_id, experience.experience_id);
        this.setState({ isPresentationDelayed: false });
      }
    }

    handleCompleteOnHover = () => {
      const { experienceClient, experience } = this.props;
      if (experience.display_data && experience.display_data.complete_on_hover) {
        experienceClient.completeExperience(experience.placement_id, experience.experience_id);
      }
    };

    handleCompleteNoClick = (evt: { event: SyntheticEvent<> }) => {
      const { experienceClient, experience, onClick } = this.props;
      if (evt && evt.event) {
        evt.event.stopPropagation();
      }
      if (onClick) {
        onClick(evt);
      }
      experienceClient.completeExperience(experience.placement_id, experience.experience_id);
    };

    handleComplete = (evt: { event: SyntheticEvent<> }) => {
      const { experienceClient, experience } = this.props;
      const { wrappedComponentRef } = this.state;
      if (evt && evt.event) {
        evt.event.stopPropagation();
      }
      if (wrappedComponentRef) {
        // in case there's no onClick, explicitly click the WrappedComponent if there's a ref
        // NOTE: DO NOT TRY AT HOME!!!! Using findDOMNode to fire events is a huge anti-pattern. The only reason we're
        // doing it here is that the Experience Framework is built to generically forward clicks regardless of the depth
        // of the tree where click handling logic is defined. If a better pattern emerges to handle this requirement, we
        // should switch to it ASAP. Ask briantran for more context.
        const anchorDOM = findDOMNode(wrappedComponentRef);
        if (anchorDOM instanceof HTMLElement) {
          anchorDOM.click();
        }
      }
      experienceClient.completeExperience(experience.placement_id, experience.experience_id);
    };

    handleDismiss = (evt: { event: SyntheticEvent<> }) => {
      // Stop click event from bubbling up as it might have unintended side effects
      // (e.g. if tooltip/flyout sits on top of a parent with a click listener).
      // Ask @kdavison (Growth NUX) for more context.
      if (evt && evt.event) {
        evt.event.stopPropagation();
      }

      const { experienceClient, experience } = this.props;
      experienceClient.dismissExperience(experience.placement_id, experience.experience_id);
    };

    handleRefUpdate = (wrappedComponent: ?React.ElementRef<C>) => {
      const { wrappedComponentRef } = this.state;
      const wrappedComponentUnmounted = !wrappedComponent;
      if (!wrappedComponentRef || wrappedComponentUnmounted) {
        // $FlowFixMe Flow v0.104.0 upgrade supression.
        this.setState({ wrappedComponentRef: wrappedComponent });
      }
    };

    renderComponentTooltip(isModal: boolean) {
      const { wrappedComponentRef } = this.state;
      const { centerTooltip, overflow } = this.props;
      return (
        <div style={{ position: 'relative' }}>
          <Touchable onMouseEnter={this.handleCompleteOnHover}>
            <WrappedComponent
              {...this.props}
              ref={this.handleRefUpdate}
              onClick={this.handleCompleteNoClick}
            />
            <LegacyToolTipPulsar
              {...this.getDisplayDataProps()}
              anchor={wrappedComponentRef}
              handleComplete={this.handleComplete}
              handleDismiss={this.handleDismiss}
              isModal={isModal}
              centerTooltip={centerTooltip}
              overflow={overflow}
            >
              <WrappedComponent
                {...this.props}
                ref={this.handleRefUpdate}
                onClick={this.handleCompleteNoClick}
              />
            </LegacyToolTipPulsar>
          </Touchable>
        </div>
      );
    }

    render() {
      const { experience, isPulsarDisplayed } = this.props;
      const { hasModal, isPresentationDelayed } = this.state;
      const shouldRenderPulsar =
        experience &&
        getExperienceAnchor(experience) === anchor &&
        !isPresentationDelayed &&
        isPulsarDisplayed;

      if (shouldRenderPulsar) {
        return (
          <Box position="relative">
            {hasModal ? this.renderComponentTooltip(true) : this.renderComponentTooltip(false)}
          </Box>
        );
      } else {
        return <WrappedComponent {...this.props} />;
      }
    }
  };
}

/**
 * TODO (hfu): add README for the usage of this file and remove this comment.
 * A HOC that will show an experience if the experience anchor string matches the anchor passed in.
 * In order to have this ability a predecessor component must be wrapped with withPlacement
 * - WrappedComponent: this is the element to be clicked on complete of the tooltip
 */
export default (anchor: string, experience?: Experience) => (
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  WrappedComponent: React.ComponentType<*>,
) => {
  const mapStateToProps = getMapStateToProps(anchor, experience);
  const WrapperComponent = getWrapperComponent(anchor, WrappedComponent);
  // $FlowFixMe: add connect<Props, OwnProps, , , _, _>() typing to remove this error
  return withExperienceClient(connect(mapStateToProps)(WrapperComponent));
};
